import {track} from '../api/analytics';
import {dateToTimestamp} from '../api/dates';
import {
  createFormResponse,
  deleteThreadTemplate,
  fetchTemplates,
  fetchThread,
  fetchThreadContent,
  fetchThreadContents,
  fetchThreadRequests,
  fetchThreads,
  fetchThreadSubs,
  fetchThreadSubsByUser,
  fetchUnsortedThreadContents,
  removeThreadContent,
  setThread,
  setThreadContent,
  setThreadSub,
  setThreadTemplate,
  stripTypenames,
  threadContentListener,
  threadContentUpdatedListener,
  threadCreatedListener,
  threadRequestListener,
  threadUpdatedListener,
  updateFormContent,
  updateThreadChain,
  updateThreadContentMessage,
  updateThreadReq,
  updateThreadSub,
  updateThreadTemplate,
} from '../api/graphql';
import {getFileURL, uploadFile} from '../api/s3';
import {sanitizeMarkdown, sendNotification} from '../utils/utils';
import createDataContext from './create-data-context';

const threadReducer = (state, action) => {
  const {
    id,
    ids,
    mapped_ids,
    thread_id,
    thread,
    threads,
    content,
    contents,
    update,
    thread_subs,
    templates,
    template,
    sub,
    nextToken,
    filter,
    thread_requests,
    request,
  } = action.payload;
  switch (action.type) {
    case 'default':
      return {...state, ...action.payload};
    case 'getSubs':
      return {
        ...state,
        thread_subs: {...state.thread_subs, ...thread_subs},
        thread_subs_loaded: true,
      };
    case 'getThreadSubs':
      return {
        ...state,
        thread_subs: {...state.thread_subs, ...thread_subs},
        thread_subs_other: {
          ...state.thread_subs_other,
          [thread_id]: ids,
        },
      };
    case 'setSub':
      return {
        ...state,
        thread_subs: {
          ...state.thread_subs,
          [thread_id]: {...state.thread_subs?.[thread_id], ...update},
        },
      };
    case 'updateSub':
      const current_sub = state.thread_subs?.[thread_id];
      return {
        ...state,
        thread_subs: {
          ...state.thread_subs,
          [thread_id]: {
            ...current_sub,
            ...update,
            previous_read: current_sub?.last_read,
          },
        },
      };
    case 'updateSystemStatus':
      return {
        ...state,
        systemStatus: {
          ...state.systemStatus,
          ...action.payload,
          timestamp: Date.now(),
        },
      };
    case 'getThreads':
      return {
        ...state,
        thread_ids: ids,
        threads: {...state.threads, ...threads},
        threads_loaded: true,
      };
    case 'getContents':
      return {
        ...state,
        content_ids: {...state.content_ids, [thread_id]: mapped_ids},
        thread_content: {...state.thread_content, ...contents},
      };
    case 'getContent':
      return {
        ...state,
        thread_content: {...state.thread_content, [id]: content},
      };
    case 'setThread':
      return {
        ...state,
        thread_ids: [id, ...state.thread_ids],
        threads: {...state.threads, [id]: thread},
      };
    case 'threadUpdate':
      return {
        ...state,
        threads: {
          ...state.threads,
          [id]: {...state.threads[id], ...thread},
        },
        thread_ids: state.thread_ids.includes(id)
          ? state.thread_ids
          : [id, ...state.thread_ids],
      };
    case 'updateListeners':
      return {
        ...state,
        listeners: {
          ...state.listeners,
          ...action.payload,
        },
      };
    case 'setContent':
      const {parent} = content;
      const current_thread = {...state.content_ids?.[thread_id]};

      if (current_thread[parent] === undefined) {
        current_thread[parent] = [];
      }
      if (parent) {
        current_thread[parent].push(id);
      } else {
        current_thread[id] = [];
      }

      return {
        ...state,
        thread_content: {...state.thread_content, [id]: content},
        content_ids: {
          ...state.content_ids,
          [thread_id]: current_thread,
        },
      };
    case 'deleteThread':
      return {...state, thread_content: {...state.thread_content, [id]: null}};
    case 'getThread':
      return {...state, threads: {...state.threads, [id]: thread}};
    case 'updateThread':
      const {status} = update;
      let filtered_ids = [...state.thread_ids];

      if (status === 'archived') {
        filtered_ids = filtered_ids.filter(uid => uid !== id);
      }
      return {
        ...state,
        threads: {
          ...state.threads,
          [id]: {...(state.threads?.[id] ?? {}), ...update},
        },
        thread_ids: filtered_ids,
      };
    case 'updateContent':
      return {
        ...state,
        thread_content: {
          ...state.thread_content,
          [id]: {...(state.thread_content?.[id] ?? {}), ...update},
        },
      };
    case 'addSub':
      return {
        ...state,
        subscriptions: [...state.subscriptions, sub],
      };
    case 'addRequestSub':
      return {
        ...state,
        request_subscriptions: {...state.subscriptions, [id]: sub},
      };
    case 'setRequest':
      return {
        ...state,
        thread_request_ids: {
          ...state.thread_request_ids,
          [thread_id]: [id, ...(state.thread_request_ids?.[thread_id] ?? [])],
        },
        thread_requests: {
          ...state.thread_requests,
          [id]: request,
        },
      };
    case 'paginateThreads':
      return {
        ...state,
        threads: {...state.threads, ...threads},
        thread_token: nextToken,
        thread_search: [...state.thread_search, ...ids],
        thread_filter: filter,
        threads_search_loaded: true,
      };
    case 'searchThreads':
      return {
        ...state,
        threads: {...state.threads, ...threads},
        thread_token: nextToken,
        thread_search: ids,
        thread_filter: filter,
        threads_search_loaded: true,
      };
    case 'getThreadRequests':
      return {
        ...state,
        thread_request_ids: {...state.thread_request_ids, [thread_id]: ids},
        thread_requests: {
          ...state.thread_requests,
          ...thread_requests,
        },
      };
    case 'updateThreadRequest':
      const {thread_id: t_id} = update;
      const request_ids = state.thread_request_ids?.[t_id]?.filter(
        req_id => req_id !== update.id,
      );
      return {
        ...state,
        thread_requests: {
          ...state.thread_requests,
          [id]: {...(state?.thread_requests?.[id] ?? {}), ...update},
        },
        thread_request_ids: {
          ...state.thread_request_ids,
          [t_id]: request_ids,
        },
      };
    case 'fetchDrafts':
      return {
        ...state,
        draft_ids: ids,
        thread_content: {...state.thread_content, ...contents},
      };
    case 'getTemplates':
      return {
        ...state,
        template_ids: [...state.template_ids, ...ids],
        templates: {...state.templates, ...templates},
        templates_loaded: true,
      };
    case 'setTemplate':
      return {
        ...state,
        template_ids: [...state.template_ids, id],
        templates: {...state.templates, [id]: template},
      };
    case 'updateTemplate':
      return {
        ...state,
        templates: {
          ...state.templates,
          [id]: {...state.templates[id], ...update},
        },
      };
    case 'deleteTemplate':
      const filtered = [...state.template_ids].filter(t_id => t_id !== id);
      return {
        ...state,
        template_ids: filtered,
        templates: {...state.templates, [id]: null},
      };
    case 'reset':
      return defaultValues;
    default:
      return state;
  }
};

const defaultUpdate = dispatch => update => {
  try {
    dispatch({type: 'default', payload: update});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getThreadSubscriptions = dispatch => async params => {
  try {
    const {items, nextToken} = await fetchThreadSubsByUser(params);

    let all_items = [...items];
    let token = nextToken;
    let i = 0;

    while (token && i < 100) {
      const {items, nextToken} = await fetchThreadSubsByUser({
        ...params,
        nextToken: token,
      });
      all_items = [...all_items, ...items];
      token = nextToken;
      i++;
    }

    const thread_subs = {};

    all_items.forEach(item => {
      const {thread_id} = item;
      thread_subs[thread_id] = item;
    });

    const payload = {thread_subs};
    dispatch({type: 'getSubs', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getThreadSubscriptionsByThread =
  dispatch => async (params, thread_id) => {
    try {
      const {items, nextToken} = await fetchThreadSubs(params);

      let all_items = [...items];
      let token = nextToken;
      let i = 0;

      while (token && i < 100) {
        const {items, nextToken} = await fetchThreadSubs({
          ...params,
          nextToken: token,
        });
        all_items = [...all_items, ...items];
        token = nextToken;
        i++;
      }

      const thread_subs = {};

      const ids = all_items.map(item => {
        const {id} = item;
        thread_subs[id] = item;
        return id;
      });
      const payload = {thread_subs, ids, thread_id};

      dispatch({type: 'getThreadSubs', payload});
      return {success: true, error: null};
    } catch (err) {
      return handleErrors(err, dispatch);
    }
  };

const updateThreadSubscription = dispatch => async update => {
  try {
    const {thread_id} = update;
    await updateThreadSub(update);

    const payload = {thread_id, update};
    dispatch({type: 'updateSub', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const createThreadSubscription = dispatch => async update => {
  try {
    const {thread_id} = update;
    await setThreadSub(update);

    const payload = {thread_id, update};
    dispatch({type: 'setSub', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getThreads = dispatch => async (params, user_id) => {
  try {
    const {items} = await fetchThreads(params);

    const sorted = items
      .filter(item => {
        const {owner_id, status, type} = item;
        if (status === 'active') {
          return true;
        }

        if (owner_id === user_id) {
          return true;
        }

        return false;
      })
      .sort((a, b) => b?.updated - a?.updated);
    const threads = {};
    const ids = await Promise.all(
      sorted.map(async item => {
        const {id, media, custom_logo} = item;
        if (media?.length) {
          const downloaded = await downloadMedia(media);
          item.media = downloaded;
        }
        if (custom_logo) {
          const {key} = custom_logo;
          const {expiresAt, url} = await getFileURL(key);
          item.custom_logo.url = url;
        }
        threads[id] = item;
        return id;
      }),
    );
    const payload = {ids, threads};
    dispatch({type: 'getThreads', payload});
    return {success: true, error: null, data: ids};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getContents = dispatch => async (params, thread_id) => {
  try {
    const {items} = await fetchThreadContents(params);

    const contents = {};
    const mapped_ids = {};
    const ids = items.map(async item => {
      const {id, parent, media} = item;
      if (media?.length) {
        const downloaded = await downloadMedia(media);
        item.media = downloaded;
      }
      contents[id] = item;
      if (parent) {
        if (mapped_ids[parent] === undefined) {
          mapped_ids[parent] = [];
        }
        mapped_ids[parent].push(id);
      } else {
        if (mapped_ids[id] === undefined) {
          mapped_ids[id] = [];
        }
      }
      return id;
    });

    const payload = {ids, mapped_ids, contents, thread_id};
    dispatch({type: 'getContents', payload});
    return {success: true, error: null, data: ids};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getContent = dispatch => async id => {
  try {
    const content = await fetchThreadContent(id);

    const payload = {id, content};
    dispatch({type: 'getContent', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const createThread = dispatch => async thread => {
  try {
    const {id, media, custom_logo, owner_id} = thread;
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'processing',
        message: 'Creating new workspace...',
        operation: 'create_thread',
      },
    });
    if (custom_logo) {
      // UPLOAD TO S3
      const {name, type, size} = custom_logo;
      const key = `${id}/${name}`;
      dispatch({
        type: 'updateSystemStatus',
        payload: {
          type: 'processing',
          message: 'Uploading workspace logo...',
          operation: 'upload_logo',
        },
      });
      const response = await uploadFile(key, custom_logo, {
        accessLevel: 'public',
        contentType: type,
        onProgress: ({transferredBytes, totalBytes}) => {},
      });

      await setThread({...thread, custom_logo: {key, type, size}});
      // const payload = {id, thread: {...thread, media: mapped_media}};
      // dispatch({type: 'setThread', payload});
    } else {
      await setThread(thread);
      // const payload = {id, thread};
      // dispatch({type: 'setThread', payload});
    }
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'success',
        message: 'Workspace created successfully',
        operation: 'create_thread',
      },
    });
    return {success: true, error: null};
  } catch (err) {
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'error',
        message: err.message || 'Failed to create workspace',
        operation: 'create_thread',
      },
    });
    return handleErrors(err, dispatch);
  }
};

const createThreadContent = dispatch => async content => {
  try {
    const {id, thread_id, owner_id, media, status} = content;
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'processing',
        message: 'Posting content...',
        operation: 'create_content',
      },
    });
    if (media?.length) {
      dispatch({
        type: 'updateSystemStatus',
        payload: {
          type: 'processing',
          message: 'Uploading media...',
          operation: 'upload_media',
        },
      });
      // UPLOAD TO S3
      const mapped_media = [];
      const uploaded_media = await Promise.all(
        media.map(async file => {
          const {name, type, size} = file;
          const key = `${id}/${name}`;
          const response = await uploadFile(key, file, {
            accessLevel: 'public',
            contentType: type,
            onProgress: ({transferredBytes, totalBytes}) => {},
          });
          mapped_media.push({
            key,
            type,
            size,
            url: URL.createObjectURL(file),
          });
          return {key, type, size};
        }),
      );

      await setThreadContent({...content, media: uploaded_media});

      // UPDATE MESSAGES
      if (status === 'active') {
        const payload = {
          id,
          thread_id,
          content: {...content, media: mapped_media},
        };
        dispatch({type: 'setContent', payload});
      }
    } else {
      await setThreadContent(content);
      if (status === 'active') {
        const payload = {id, thread_id, content};
        dispatch({type: 'setContent', payload});
      }
    }
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'success',
        message: 'Content posted successfully',
        operation: 'create_content',
      },
    });
    const update = {
      id: thread_id,
      updated: dateToTimestamp(),
      last_sender: owner_id,
      last_content: id,
    };
    await updateThreadChain(update);
    track('thread_content_created', {content});

    const payload = {id, update};
    dispatch({type: 'updateThread', payload});
    return {success: true, error: null};
  } catch (err) {
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'error',
        message: err.message || 'Failed to post content',
        operation: 'create_content',
      },
    });
    return handleErrors(err, dispatch);
  }
};

const deleteThreadContent = dispatch => async content => {
  try {
    const {id, thread_id, owner_id} = content;
    await removeThreadContent(content);
    const payload = {id, content};
    dispatch({type: 'deleteThread', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getThread = dispatch => async thread_id => {
  try {
    const thread = await fetchFullThread(thread_id);

    if (!thread) {
      return;
    }

    const {id, custom_logo} = thread;
    if (custom_logo) {
      const {key} = custom_logo;
      const {expiresAt, url} = await getFileURL(key);
      thread.custom_logo.url = url;
    }

    const payload = {id, thread: stripTypenames(thread)};
    dispatch({type: 'getThread', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const updateThread = dispatch => async update => {
  try {
    const {id, custom_logo} = update;
    // UPLOAD TO S3
    if (custom_logo) {
      const {name, type, size} = custom_logo;
      const key = `${id}/${name}`;
      const response = await uploadFile(key, custom_logo, {
        accessLevel: 'public',
        contentType: type,
        onProgress: ({transferredBytes, totalBytes}) => {},
      });
      update.custom_logo = {
        key,
        type,
        size,
      };
      await updateThreadChain(stripTypenames(update));
      const payload = {
        id,
        update: {
          ...update,
          custom_logo: {key, type, size, url: URL.createObjectURL(custom_logo)},
        },
      };
      dispatch({type: 'updateThread', payload});
    } else {
      await updateThreadChain(stripTypenames(update));
      const payload = {id, update};
      dispatch({type: 'updateThread', payload});
    }

    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const updateContent = dispatch => async update => {
  try {
    const {id, media} = update;

    if (media?.length) {
      // DATA FOR CONTEXT
      const mapped = [];
      // DATA FOR DYNAMO
      const uploaded_media = await Promise.all(
        media.map(async file => {
          const {name, type, size} = file;
          // IF ALREADY UPLOADED, RETURN AS IS
          if (file.key) {
            mapped.push(file);
            return {key: file.key, type, size};
          }

          const key = `${id}/${name}`;
          const response = await uploadFile(key, file, {
            accessLevel: 'public',
            contentType: type,
            onProgress: ({transferredBytes, totalBytes}) => {},
          });
          mapped.push({key, type, size, url: URL.createObjectURL(file)});
          return {key, type, size};
        }),
      );
      await updateThreadContentMessage({...update, media: uploaded_media});
      const payload = {id, update: {...update, media: mapped}};
      dispatch({type: 'updateContent', payload});
    } else {
      await updateThreadContentMessage(update);

      const payload = {id, update};
      dispatch({type: 'updateContent', payload});
    }
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const attachThreadContentListener = dispatch => async (vars, user_id) => {
  try {
    const onCreateEvent = async ({value}) => {
      dispatch({
        type: 'updateSystemStatus',
        payload: {
          type: 'processing',
          message: 'New content received...',
          operation: 'content_listener',
        },
      });
      const stripped = stripTypenames(value);
      const {id, content, thread_id, owner_id, media} = stripped;
      // IGNORE OUTGOING MESSAGES, HANDLED ELSEWHERE
      if (owner_id === user_id) {
        dispatch({
          type: 'updateSystemStatus',
          payload: {
            type: 'success',
            message: 'Content synchronized',
            operation: 'content_listener',
          },
        });
        return;
      }
      if (media?.length) {
        const downloaded = await downloadMedia(media);
        stripped.media = downloaded;
      }
      // UPDATE MESSAGES
      const payload = {id, thread_id, content: stripped};
      dispatch({type: 'setContent', payload});

      await sendNotification({
        title: `New Message from ${owner_id.split('@')[0]}`,
        content: sanitizeMarkdown(content),
        thread_id,
        id,
      });

      // UPDATE THREAD OBJECT
      // const thread = await fetchThread(thread_id);
      // if (thread) {
      //   const payload = {id: thread.id, thread};
      //   dispatch({type: 'getThread', payload});
      // }
      dispatch({
        type: 'updateSystemStatus',
        payload: {
          type: 'success',
          message: 'Content synchronized',
          operation: 'content_listener',
        },
      });
    };

    const onUpdateEvent = async ({value}) => {
      dispatch({
        type: 'updateSystemStatus',
        payload: {
          type: 'processing',
          message: 'New content received...',
          operation: 'content_listener',
        },
      });
      const stripped = stripTypenames(value);
      const {id, media, owner_id} = stripped;
      // IGNORE OUTGOING MESSAGES, HANDLED ELSEWHERE
      if (owner_id === user_id) {
        dispatch({
          type: 'updateSystemStatus',
          payload: {
            type: 'success',
            message: 'Content synchronized',
            operation: 'content_listener',
          },
        });
        return;
      }
      if (media?.length) {
        const downloaded = await downloadMedia(media);
        stripped.media = downloaded;
      }
      // UPDATE MESSAGES
      const payload = {id, update: stripped};
      dispatch({type: 'updateContent', payload});
      dispatch({
        type: 'updateSystemStatus',
        payload: {
          type: 'success',
          message: 'Content synchronized',
          operation: 'content_listener',
        },
      });
    };

    const createSub = threadContentListener(vars, onCreateEvent);
    const updateSub = threadContentUpdatedListener(vars, onUpdateEvent);

    const payload = {
      sub: {create: createSub, update: updateSub},
    };
    dispatch({type: 'addSub', payload});
    dispatch({
      type: 'updateListeners',
      payload: {content: true},
    });
  } catch (err) {
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'error',
        message: 'Failed to attach content listener',
        operation: 'content_listener',
      },
    });
    return handleErrors(err, dispatch);
  }
};

const attachThreadRequestListener = dispatch => async (vars, thread_id) => {
  try {
    if (!thread_id) {
      return;
    }

    const onEvent = async ({value}) => {
      const {id} = value;
      const payload = {id, thread_id, request: value};
      dispatch({type: 'setRequest', payload});
    };

    const sub = threadRequestListener(vars, onEvent);
    const payload = {sub, id: thread_id};
    dispatch({type: 'addRequestSub', payload});
    dispatch({
      type: 'updateListeners',
      payload: {request: true},
    });
  } catch (err) {
    dispatch({
      type: 'updateSystemStatus',
      payload: {
        type: 'error',
        message: 'Failed to attach request listener',
        operation: 'content_listener',
      },
    });
    return handleErrors(err, dispatch);
  }
};

const attachThreadListener = dispatch => async vars => {
  try {
    const onCreate = async ({value}) => {
      const stripped = stripTypenames(value);
      const {id, custom_logo} = stripped;
      if (custom_logo) {
        const {key} = custom_logo;
        const {expiresAt, url} = await getFileURL(key);
        stripped.custom_logo.url = url;
      }
      const payload = {id, thread: stripped};
      dispatch({type: 'setThread', payload});
    };
    const onUpdate = async ({value}) => {
      const stripped = stripTypenames(value);
      const {id, custom_logo} = stripped;
      if (custom_logo) {
        const {key} = custom_logo;
        const {expiresAt, url} = await getFileURL(key);
        stripped.custom_logo.url = url;
      }
      const payload = {id, thread: stripped};

      dispatch({type: 'threadUpdate', payload});
    };

    const created = threadCreatedListener(vars, onCreate);
    const updated = threadUpdatedListener(vars, onUpdate);
    dispatch({
      type: 'updateListeners',
      payload: {thread: true},
    });
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const searchThreads = dispatch => async (filter_obj, options) => {
  try {
    const {limit, nextToken} = options || {};
    if (!nextToken) {
      dispatch({
        type: 'default',
        payload: {thread_search: [], thread_filter: filter_obj},
      });
    } else {
      dispatch({type: 'default', payload: {thread_filter: filter_obj}});
    }

    const filter = threadToFilter(filter_obj);

    const {items, nextToken: first_token} = await fetchThreads({
      ...options,
      filter,
    });

    let all_items = [...items];
    let token = first_token;
    let i = 0;

    while (all_items.length < limit && token && i < 100) {
      const {items, nextToken} = await fetchThreads({
        limit,
        nextToken: token,
        filter,
      });
      all_items = [...all_items, ...items];
      token = nextToken;
      i++;
    }

    const threads = {};
    const ids = await Promise.all(
      all_items.map(async thread => {
        const {id, custom_logo} = thread;
        if (custom_logo) {
          const {key} = custom_logo;
          const {expiresAt, url} = await getFileURL(key);
          thread.custom_logo.url = url;
        }

        threads[id] = thread;
        return id;
      }),
    );

    const payload = {ids, threads, filter: filter_obj, nextToken: token};

    if (nextToken) {
      dispatch({type: 'paginateThreads', payload});
    } else {
      dispatch({type: 'searchThreads', payload});
    }
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getThreadRequests = dispatch => async query => {
  try {
    const {thread_id} = query;
    const {items} = await fetchThreadRequests(query);

    const thread_requests = {};
    const ids = items.map(item => {
      const {id} = item;
      thread_requests[id] = item;
      return id;
    });
    const payload = {ids, thread_requests, thread_id};
    dispatch({type: 'getThreadRequests', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const updateThreadRequest = dispatch => async update => {
  try {
    const {id} = update;
    await updateThreadReq(update);

    const payload = {id, update};
    dispatch({type: 'updateThreadRequest', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const fetchDrafts = dispatch => async query => {
  try {
    const {items, nextToken} = await fetchUnsortedThreadContents(query);

    let all_items = [...items];
    let token = nextToken;
    let i = 0;

    while (token && i < 100) {
      const {items, nextToken} = await fetchUnsortedThreadContents({
        ...query,
        nextToken: token,
      });
      all_items = [...all_items, ...items];
      token = nextToken;
      i++;
    }

    const contents = {};
    const ids = all_items.map(item => {
      const {id} = item;
      contents[id] = item;
      return id;
    });
    const payload = {ids, contents};
    dispatch({type: 'fetchDrafts', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const getTemplates = dispatch => async query => {
  try {
    const {items} = await fetchTemplates(query);

    const templates = {};
    const ids = items.map(item => {
      const {id} = item;
      templates[id] = item;
      return id;
    });
    const payload = {ids, templates};
    dispatch({type: 'getTemplates', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const createTemplate = dispatch => async template => {
  try {
    const {id} = template;
    await setThreadTemplate(template);

    const payload = {id, template};
    dispatch({type: 'setTemplate', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const updateTemplate = dispatch => async update => {
  try {
    const {id} = update;
    await updateThreadTemplate(update);

    const payload = {id, update};
    dispatch({type: 'updateTemplate', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const deleteTemplate = dispatch => async template => {
  try {
    const {id} = template;
    await deleteThreadTemplate(template);

    const payload = {id};
    dispatch({type: 'deleteTemplate', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const resetThreads = dispatch => async query => {
  try {
    dispatch({type: 'reset', payload: {}});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const downloadMedia = async media => {
  try {
    if (media?.length) {
      const downloaded = await Promise.all(
        media.map(async file => {
          const {key} = file;
          const {expiresAt, url} = await getFileURL(key);
          return {...file, url};
        }),
      );
      return downloaded;
    }
    return [];
  } catch (err) {
    throw err;
  }
};

const fetchFullThread = async (id, options) => {
  try {
    const thread = await fetchThread(id, options);
    if (thread) {
      const {media} = thread;
      // FETCH THE PROFILE URL
      if (media?.length) {
        const downloaded = await downloadMedia(media);
        thread.media = downloaded;
      }
      return thread;
    } else {
      return null;
    }
  } catch (err) {
    throw err;
  }
};
const submitFormResponse = dispatch => async (contentId, response) => {
  try {
    const {id, thread_id} = response;

    await createFormResponse(thread_id, contentId, {
      user_id: response.user_id,
      timestamp: dateToTimestamp(),
      answers: response.answers,
    });

    const payload = {
      id: contentId,
      update: {
        form_responses: response,
      },
    };

    dispatch({type: 'updateContent', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};

const updateFormItems = dispatch => async (contentId, formItems) => {
  try {
    await updateFormContent(contentId, formItems);

    const payload = {
      id: contentId,
      update: {
        form_items: formItems,
      },
    };

    dispatch({type: 'updateContent', payload});
    return {success: true, error: null};
  } catch (err) {
    return handleErrors(err, dispatch);
  }
};
const default_thread_filter = {
  title: '',
  content: '',
  tags: [],
  type: 'thread',
  status: ['active', 'complete'],
  members: [],
  not_members: [],
};

const defaultValues = {
  thread_ids: [],
  threads: {},
  content_ids: {},
  thread_content: {},
  thread_subs: {},
  thread_subs_other: {},
  subscriptions: [],
  request_subscriptions: {},
  thread_request_ids: {},
  thread_requests: {},
  draft_ids: [],
  template_ids: [],
  templates: {},
  templates_loaded: false,
  threads_loaded: false,
  thread_subs_loaded: false,
  thread_filter: default_thread_filter,
  thread_search: [],
  thread_token: null,
  threads_search_loaded: false,
  error: null,
  systemStatus: {
    type: 'ready', // 'ready' | 'processing' | 'error' | 'success'
    message: '',
    timestamp: null,
    operation: null,
  },
  listeners: {
    thread: false,
    content: false,
    request: false,
  },
};

export const {Provider, Context} = createDataContext(
  threadReducer,
  {
    defaultUpdate,
    submitFormResponse,
    getThreadSubscriptions,
    updateThreadSubscription,
    updateFormItems,
    createThreadSubscription,
    getThreads,
    getContents,
    getContent,
    createThread,
    createThreadContent,
    deleteThreadContent,
    getThread,
    updateThread,
    updateContent,
    attachThreadContentListener,
    attachThreadRequestListener,
    attachThreadListener,
    searchThreads,
    getThreadRequests,
    updateThreadRequest,
    fetchDrafts,
    getTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    resetThreads,
    getThreadSubscriptionsByThread,
  },
  defaultValues,
);

const handleErrors = (err, dispatch) => {
  const {data, errors, code} = err;
  console.log('THREAD ERROR', err);
  let error = 'Something went wrong.';

  if (code) {
    switch (code) {
      default:
        break;
    }
  }

  const top_error = errors && errors[0];
  if (top_error) {
    switch (top_error.errorType) {
      case 'DynamoDB:ConditionalCheckFailedException':
        error = 'Item already exists.';
        break;
      default:
        break;
    }

    if (
      top_error?.message?.includes(
        'Item size has exceeded the maximum allowed size',
      )
    ) {
      error = 'Your post is too long. Try shortening your post.';
    }
  }

  dispatch({
    type: 'error',
    payload: error,
  });
  return {success: false, error};
};

const threadToFilter = obj => {
  const and = [{security_level: {ne: 'secure'}}];
  const or = [];
  const fields = Object.keys(obj);
  fields.forEach(field => {
    const values = obj[field];

    // IGNORE NULL
    if (!values) {
      return;
    }

    switch (field) {
      case 'title':
      case 'content':
        and.push({[field]: {contains: values}});
        break;
      case 'type':
        and.push({[field]: {eq: values}});
        break;
      case 'status':
        values.forEach(val => {
          or.push({[field]: {eq: val}});
        });
        // and.push({[field]: {eq: values}});
        break;
      case 'tags':
      case 'members':
        values.forEach(val => {
          and.push({[field]: {contains: val}});
        });
        break;
      case 'not_members':
        values.forEach(val => {
          and.push({members: {notContains: val}});
        });
        break;
      default:
        return;
    }
  });
  const filter = {};
  if (and.length) {
    filter.and = and;
  }
  if (or.length) {
    filter.or = or;
  }

  return filter;
};
